import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ScannerSection, type ScannerSectionHandle } from '../scanner';
import type { ScanCaptureResult } from '../scanner/scanFlash';
import OrderDetailCameraBar from './orderdetail/OrderDetailCameraBar';
import PackingPhotoSheet, { PACKING_PHOTO_PHASES } from './orderdetail/PackingPhotoSheet';
import './orderdetail/pick-list.css';
import './NextOrderPicker.css';

const SUCCESS_FLASH_MS = 1000;

export interface OrderLabelScanSheetProps {
  readonly title: string;
  readonly heading: ReactNode;
  readonly closeLabel: string;
  readonly shutterLabel?: string;
  readonly error: string;
  readonly submitting: boolean;
  readonly busyLabel: string;
  readonly onCapture: (qrData: string | null) => ScanCaptureResult;
  readonly onClose: () => void;
  readonly onSuccessNavigate: () => void;
  readonly extraAction?: ReactNode;
}

export function OrderLabelScanSheet({
  title,
  heading,
  closeLabel,
  shutterLabel = 'Scan label',
  error,
  submitting,
  busyLabel,
  onCapture,
  onClose,
  onSuccessNavigate,
  extraAction,
}: OrderLabelScanSheetProps): ReactElement {
  const [webcamOn, setWebcamOn] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const scannerRef = useRef<ScannerSectionHandle>(null);
  const confirmedRef = useRef(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    if (confirmedRef.current || submitting) return false;
    const ok = await onCapture(qrData);
    if (ok === true) {
      confirmedRef.current = true;
      setShowSuccess(true);
      if (successTimerRef.current != null) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        onSuccessNavigate();
      }, SUCCESS_FLASH_MS);
      return true;
    }
    return false;
  };

  return (
    <div className="next-order-label-scan">
      <PackingPhotoSheet
        title={title}
        heading={heading}
        closeLabel={closeLabel}
        phase={PACKING_PHOTO_PHASES.LIVE}
        stillSrc={null}
        submitting={submitting}
        busyLabel={busyLabel}
        error={error}
        camera={
          <ScannerSection
            ref={scannerRef}
            mode="qr"
            onCapture={handleQrCapture}
            webcamOn={webcamOn}
            setWebcamOn={setWebcamOn}
            className="webcam-wrapper"
          />
        }
        onClose={onClose}
      />
      <OrderDetailCameraBar
        open
        suppressSheet
        shutterLabel={shutterLabel}
        onShutter={() => {
          if (submitting || confirmedRef.current) return;
          scannerRef.current?.capture();
        }}
      />
      {extraAction}
      {showSuccess ? (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}
    </div>
  );
}
